<template>
  <div>
    <span
      :class="[
        statusClass,
        { 'cursor-pointer': isClickable && canManageEvent },
        { 'text-nowrap': isIconCreditState },
        'd-inline-flex',
        'align-items-center'
      ]"
      v-b-tooltip.hover="tooltip"
      @click="showModal"
    >
      {{ text }}
    </span>

    <!-- Action Modal -->
    <b-modal
      :id="`action-modal-${eventId}`"
      ref="actionModal"
      :title="getModalTitle()"
      hide-footer
      @cancel="closeModal"
    >
      <p>{{ getModalMessage() }}</p>

      <div class="d-flex justify-content-center mt-3 mb-1">
        <b-button
          variant="success"
          class="mr-2"
          @click="confirmCredit"
        >
          Credit Now
          <span v-if="currentHidden">and Unhide</span>
        </b-button>
        <b-button
          v-if="!currentHidden"
          variant="warning"
          class="mr-2"
          @click="hideEvent"
        >
          Hide Event
        </b-button>
        <b-button
          variant="secondary"
          @click="closeModal"
        >
          Cancel
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'EventCreditStatusElement',

  props: {
    eventId:      { type: [String, Number], required: true },
    creditStatus: { type: Number, required: true },
    status:       { type: Number, required: true },
    creditDate:   { type: String, default: null },
    isHidden:     { type: [Boolean, Number], default: false },
    serverTime:   { type: String, required: true },
    disqualificationFlag: { type: [Boolean, Number, String], default: null },
    disqualificationReason: { type: String, default: null },
    publisherTransactionId: { type: [String, Number], default: null },
    rewardType: { type: Number, default: null }
  },

  data () {
    return {
      currentCreditStatus: this.creditStatus,
      currentCreditDate: this.creditDate,
      currentHidden: !!this.isHidden,
      currentServerTime: this.serverTime,
      currentStatus: this.status,
      currentDisqualificationFlag: this.disqualificationFlag,
      currentDisqualificationReason: this.disqualificationReason,
      currentPublisherTransactionId: this.publisherTransactionId,
      currentRewardType: this.rewardType,
      selectedAction: null
    }
  },

  computed: {
    isIconCreditState () {
      return this.currentDisqualificationFlag === null && this.currentCreditStatus === 1
    },
    canManageEvent () {
      return this.canAccessPageByPermissionModules(['internal.offer_user_event.credit_now'])
    },

    isClickable () {
      if (!this.canManageEvent) return false

      // IN_PROGRESS (threshold reward not yet met) is never manually creditable
      if (this.currentCreditStatus === 6) return false

      // Allow clicking for hidden events with a disqualification flag
      if (this.currentHidden && this.currentDisqualificationFlag !== null) return true

      // Don't allow clicking if disqualified and not hidden
      if (this.currentDisqualificationFlag !== null) return false

      // credit_status=1 install event without publisher transaction → not clickable (no transaction expected)
      if (this.currentCreditStatus === 1 && this.currentRewardType === 1 && this.currentPublisherTransactionId === null && this.currentCreditDate && new Date(this.currentCreditDate) <= new Date(this.currentServerTime)) return false

      // credit_status=1 but no publisher transaction → allow clicking (not truly paid)
      if (this.currentCreditStatus === 1 && this.currentPublisherTransactionId === null) return true

      // Block if credit_status=1 with a verified publisher transaction (already paid)
      if (this.currentCreditStatus !== 0) return false

      // Rejected & Overdue: only block if a publisher transaction exists (proven payment)
      if (this.currentCreditStatus === 0 && this.currentStatus === 2 && (!this.currentHidden && this.currentCreditDate && new Date(this.currentCreditDate) <= new Date(this.currentServerTime)) && this.currentPublisherTransactionId !== null) return false

      return true
    },
    // PENDING = 0;
    // COMPLETED = 1;
    // RECONCILED = 2;
    // TO_BE_RECONCILED = 3;
    statusClass () {
      // Priority: If disqualification_flag is not null, show the disqualification reason in red
      if (this.currentDisqualificationFlag !== null) return 'text-danger'

      if (this.currentCreditStatus === 6) return 'text-primary'
      if (this.currentCreditStatus === 2) return 'text-amber'
      if (this.currentCreditStatus === 3) return 'text-info'
      if (this.currentCreditStatus === 4) return 'text-warning'
      if (this.currentCreditStatus === 7) return 'text-success'

      // credit_status=1 install event without publisher transaction → success (no transaction expected)
      if (this.currentCreditStatus === 1 && this.currentRewardType === 1 && this.currentPublisherTransactionId === null && this.currentCreditDate && new Date(this.currentCreditDate) <= new Date(this.currentServerTime)) return 'text-success'
      // credit_status=1 but no publisher transaction → warning (not truly paid)
      if (this.currentCreditStatus === 1 && this.currentPublisherTransactionId === null) return 'text-warning'
      if (this.currentCreditStatus === 1) return 'text-success'
      if (this.currentCreditStatus === 0 && this.currentHidden) return 'text-warning'
      if (this.currentCreditStatus === 0 && this.currentStatus === 2) return 'text-danger'
      if (this.currentCreditStatus === 0 && [0, 1].includes(this.currentStatus))  return 'text-yellow'
      return 'text-secondary'
    },

    text () {
      // Priority: If disqualification_flag is not null, show the disqualification reason
      if (this.currentDisqualificationFlag !== null) {
        return this.currentDisqualificationReason || 'Disqualified'
      }

      if (this.currentCreditStatus === 6) return 'In Progress'
      if (this.currentCreditStatus === 2) return 'Ready for Processing'
      if (this.currentCreditStatus === 3) return 'In Earning Jar'
      if (this.currentCreditStatus === 4) return 'Unclaimed Bonus Reward'
      if (this.currentCreditStatus === 7) return 'Manually Paid'

      // credit_status=1 install event without publisher transaction → done (no transaction expected)
      if (this.currentCreditStatus === 1 && this.currentRewardType === 1 && this.currentPublisherTransactionId === null && this.currentCreditDate && new Date(this.currentCreditDate) <= new Date(this.currentServerTime)) return 'Credited ✅'
      // credit_status=1 but no publisher transaction → not paid
      if (this.currentCreditStatus === 1 && this.currentPublisherTransactionId === null) return 'Credited ❌'
      if (this.currentCreditStatus === 1) return 'Credited ✅'
      if (this.currentCreditStatus === 0 && this.currentHidden) return 'Hidden'
      if (this.currentCreditStatus === 0 && this.currentStatus === 2) return 'Rejected'
      if (this.currentCreditStatus === 0 && [0, 1].includes(this.currentStatus) && this.currentCreditDate && new Date(this.currentCreditDate) <= new Date(this.currentServerTime)) return 'Crediting now...'
      if (this.currentCreditStatus === 0 && [0, 1].includes(this.currentStatus)) return 'Pending'
      return 'Unknown'
    },

    tooltip () {
      // Priority: If disqualification_flag is not null, show disqualified
      if (this.currentDisqualificationFlag !== null) {
        return 'Disqualified'
      }

      if (this.currentCreditStatus === 6) {
        return 'User is working toward the reward threshold — no payment yet.'
      }

      // credit_status=1 install event without publisher transaction → done (no transaction expected)
      if (this.currentCreditStatus === 1 && this.currentRewardType === 1 && this.currentPublisherTransactionId === null && this.currentCreditDate && new Date(this.currentCreditDate) <= new Date(this.currentServerTime)) {
        return 'Done — Install event'
      }

      // credit_status=1 with publisher transaction → paid
      if (this.currentCreditStatus === 1 && this.currentPublisherTransactionId !== null) {
        return 'Paid via publisher transaction'
      }

      // credit_status=1 but no publisher transaction → not paid
      if (this.currentCreditStatus === 1 && this.currentPublisherTransactionId === null && this.canManageEvent) {
        return 'Not paid — no publisher transaction found.'
      }
      if (this.currentCreditStatus === 1 && this.currentPublisherTransactionId === null) {
        return 'Not paid — no publisher transaction found.'
      }

      if (this.currentCreditStatus === 2) {
        return 'Queued for payment — the crediting worker will create the transaction and fire the postback shortly.'
      }

      if (this.currentCreditStatus === 0 && this.currentHidden && this.canManageEvent) {
        return this.currentStatus === 2 ? 'Click to unhide and pay' : 'Click to unhide, despite rejection and pay'
      }
      if (this.currentCreditStatus === 0 && this.canManageEvent && this.currentStatus === 2) {
        const txInfo = this.currentPublisherTransactionId ? 'Payment verified via publisher transaction.' : 'No payment transaction found — user may not have been paid.'
        return `Click to credit despite rejection. ${txInfo}`
      }
      if (this.currentCreditStatus === 0 && [0, 1].includes(this.currentStatus) && !this.currentHidden && this.currentCreditDate && (new Date(this.currentCreditDate) > new Date(this.currentServerTime)) && this.canManageEvent) {
        return `Credit date: ${this.currentCreditDate || 'N/A'} - Click to choose action`
      }
      if (this.currentCreditStatus === 0 && [0, 1].includes(this.currentStatus) && !this.currentHidden) {
        return `Credit date: ${this.currentCreditDate || 'N/A'}`
      }
      return ''
    }
  },

  watch: {
    creditStatus: 'syncCurrentState',
    creditDate: 'syncCurrentState',
    isHidden: 'syncCurrentState',
    serverTime: 'syncCurrentState',
    status: 'syncCurrentState',
    disqualificationFlag: 'syncCurrentState',
    disqualificationReason: 'syncCurrentState',
    publisherTransactionId: 'syncCurrentState',
    rewardType: 'syncCurrentState'
  },

  methods: {
    syncCurrentState () {
      this.currentCreditStatus = this.creditStatus
      this.currentCreditDate = this.creditDate
      this.currentHidden = !!this.isHidden
      this.currentServerTime = this.serverTime
      this.currentStatus = this.status
      this.currentDisqualificationFlag = this.disqualificationFlag
      this.currentDisqualificationReason = this.disqualificationReason
      this.currentPublisherTransactionId = this.publisherTransactionId
      this.currentRewardType = this.rewardType
    },

    emitCurrentRowState () {
      this.$emit('eventUpdated', {
        status: this.currentStatus,
        credit_status: this.currentCreditStatus,
        credit_date: this.currentCreditDate,
        is_hidden: this.currentHidden ? 1 : 0,
        server_time: this.currentServerTime,
        disqualification_flag: this.currentDisqualificationFlag,
        disqualification_reason: this.currentDisqualificationReason,
        publisher_transaction_id: this.currentPublisherTransactionId,
        reward_type: this.currentRewardType
      })
    },

    showModal () {
      if (!this.canManageEvent) return

      // IN_PROGRESS (threshold reward not yet met) is never manually creditable
      if (this.currentCreditStatus === 6) return

      if (this.currentHidden && this.currentDisqualificationFlag !== null) {
        this.$refs.actionModal.show()
        return
      }

      // Block install events without publisher transaction (no transaction expected, already done)
      if (this.currentCreditStatus === 1 && this.currentRewardType === 1 && this.currentPublisherTransactionId === null && this.currentCreditDate && new Date(this.currentCreditDate) <= new Date(this.currentServerTime)) return

      // Allow for credit_status=1 with no publisher transaction (not truly paid)
      if (this.currentCreditStatus === 1 && this.currentPublisherTransactionId === null) {
        this.$refs.actionModal.show()
        return
      }

      if (this.currentCreditStatus !== 0) return

      // Rejected & Overdue: only block if publisher transaction exists (proven payment)
      if (this.currentCreditStatus === 0 && this.currentStatus === 2 && (!this.currentHidden && this.currentCreditDate && new Date(this.currentCreditDate) <= new Date(this.currentServerTime)) && this.currentPublisherTransactionId !== null) return

      this.$refs.actionModal.show()
    },

    getModalTitle () {
      return 'Choose Action'
    },

    getModalMessage () {
      if (this.currentHidden) {
        return 'This will credit now, unhide the event and clear any disqualification flags. Are you sure you want to proceed?'
      }
      if (this.currentCreditStatus === 1 && this.currentPublisherTransactionId === null) {
        return '⚠️ This event is marked as Credited but no publisher transaction exists — the user may NOT have been paid. Crediting now will re-process payment. What would you like to do with this event?'
      }
      if (this.currentStatus === 2) {
        const txMsg = this.currentPublisherTransactionId ? '⚠️ A publisher transaction exists — this event appears to have been paid.' : '⚠️ No publisher transaction found — this event may NOT have been paid.'
        return `${txMsg} What would you like to do with this event?`
      }
      return 'What would you like to do with this event?'
    },

    closeModal () {
      this.$refs.actionModal.hide()
    },

    confirmCredit () {
      if (!this.canManageEvent) return

      this.showLoading()

      this.$http
        .post(`/api/offer-events/${this.eventId}/credit-now`)
        .then(({ data }) => {
          const { credit_date, credit_status } = data
          if (credit_date) {
            this.currentCreditDate = credit_date
            this.currentServerTime = credit_date
            this.currentHidden = false
            this.currentStatus = 1
            this.currentDisqualificationFlag = null
            this.currentDisqualificationReason = null

            // Backend now returns READY_FOR_PROCESSING (2) so the chip reflects
            // the real state (queued for payment) instead of pretending it's paid
            if (credit_status !== undefined) {
              this.currentCreditStatus = credit_status
            }

            this.emitCurrentRowState()
            this.$emit('statusChanged', this.currentStatus)
            this.alertSuccess(data.message || 'Credit updated successfully')
          }
          this.closeModal()
        })
        .catch((error) => {
          this.alertError(error.response?.data?.message || 'Failed to update credit.')
        })
        .finally(() => {
          this.hideLoading()
        })
    },

    hideEvent () {
      if (!this.canManageEvent) return

      this.showLoading()

      this.$http
        .post(`/api/offer-events/${this.eventId}/hide`)
        .then(() => {
          this.currentHidden = true
          this.currentDisqualificationFlag = 7
          this.currentDisqualificationReason = 'Hidden by admin'

          this.emitCurrentRowState()
          this.alertSuccess('Event hidden successfully')
          this.closeModal()
        })
        .catch(() => {
          this.alertError('Failed to hide event.')
        })
        .finally(() => {
          this.hideLoading()
        })
    }
  }
}
</script>


<style scoped>
.text-yellow {
  color: yellow;
}
.text-amber {
  color: #ffb020;
}
</style>
